import { useTranslation } from "react-i18next"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { LotStatus } from "@/features/lots/api/types"
import { cn } from "@/lib/utils"

interface LotFillRingProps {
  current: number
  target: number
  ready: boolean
  lotStatus: LotStatus
  // Optional richer details surfaced via the hover tooltip. The ring
  // itself only renders the bag count + ratio.
  netMT?: number
  avgBagKg?: number | null
  size?: number
}

// Compact circular fill indicator. Hovering reveals the full
// breakdown (percentage, remaining bags, net weight, avg bag) so the
// card body can stay quiet.
export function LotFillRing({
  current,
  target,
  ready,
  lotStatus,
  netMT,
  avgBagKg,
  size = 96,
}: LotFillRingProps) {
  const { t } = useTranslation("lots")
  const stroke = 8
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const ratio = target > 0 ? Math.min(1, current / target) : 0
  const dashOffset = circumference * (1 - ratio)
  const pct = Math.round(ratio * 100)
  const remaining = Math.max(0, target - current)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="group relative shrink-0 cursor-default"
          style={{ width: size, height: size }}
          role="img"
          aria-label={t("detail.summary.ringAria", { current, target, pct })}
        >
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="-rotate-90"
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              strokeWidth={stroke}
              className="fill-none stroke-muted"
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              strokeWidth={stroke}
              strokeLinecap="round"
              className={cn(
                "fill-none transition-[stroke-dashoffset] duration-500",
                ready ? "stroke-emerald-500" : "stroke-amber-500",
              )}
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
            <span className="text-2xl font-semibold tabular-nums">
              {current}
            </span>
            <span className="text-muted-foreground mt-1 text-[10px] tabular-nums">
              / {target}
            </span>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="flex flex-col gap-1 px-3 py-2">
        <span className="text-[11px] font-medium">
          {t("detail.summary.ringTooltipFill", { pct, current, target })}
        </span>
        <span className="text-[11px] opacity-80">
          {lotStatus === "COMPLETED"
            ? t("detail.summary.ringTooltipCompleted")
            : lotStatus === "RECONDITIONED"
              ? t("detail.summary.ringTooltipReconditioned")
              : ready
                ? t("detail.summary.ringTooltipReady")
                : t("detail.summary.ringTooltipNeeds", { remaining })}
        </span>
        {netMT !== undefined ? (
          <span className="text-[11px] opacity-80 tabular-nums">
            {t("detail.summary.netWeight")} · {netMT.toFixed(3)} MT
          </span>
        ) : null}
        {avgBagKg ? (
          <span className="text-[11px] opacity-80 tabular-nums">
            {t("detail.summary.avgBag")} · {avgBagKg.toFixed(2)} kg
          </span>
        ) : null}
      </TooltipContent>
    </Tooltip>
  )
}
